import { useEffect, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import PropTypes from "prop-types";
import SvgIcon from "../../SvgIcon";
import classNames from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0),
    },
  },
  optionContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1.5),
    backgroundColor: theme.palette.background.white,
    borderRadius: theme.borderRadius.default,
    border: "1px solid " + theme.palette.border.main,
    cursor: "pointer",
    "&.checked, &:hover": {
      background: theme.palette.button.main,
    },
    "&.checked $icon path, &.checked $iconCustom path": {
      fill: theme.palette.text.secondary,
    },
    "&:hover $icon path, &:hover $iconCustom path": {
      fill: theme.palette.text.secondary,
    },
    "&.checked span, &:hover span": {
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightBold,
    },
    "&.checked span": {
      fontWeight: theme.typography.fontWeightBold,
      height: "3.3rem",
      display: "flex",
      alignItems: "center",
    },
  },
  icon: {
    "& path": {
      fill: theme.palette.primary.main,
    },
  },
  iconCustomWrapper: {
    display: "inline-flex",
    width: "30px",
    paddingTop: theme.spacing(0.25),
    marginRight: theme.spacing(0.75),
  },
  iconCustom: {
    margin: theme.spacing(0, "auto"),
    "& path": {
      fill: theme.palette.text.alternative,
    },
  },
}));

const RadioField = ({ name, label, choices = [], onChange }) => {
  const classes = useStyles();
  const { register, unregister, watch, setValue } = useFormContext();
  const watchValue = watch(name) || null;

  useEffect(() => {
    register(name);

    return () => {
      unregister(name, { keepValue: true });
    };
  }, [name, register, unregister]);

  const handleChange = useCallback(
    (newValue) => {
      setValue(name, newValue);
      onChange(name, newValue);
    },
    [name, setValue, onChange]
  );

  const handleSelect = useCallback(
    (event) => {
      handleChange(event.target.value);
    },
    [handleChange]
  );

  const handleClick = useCallback(
    (key) => {
      return () => {
        handleChange(key);
      };
    },
    [handleChange]
  );

  const Icon = ({ type, classes, text }) => {
    return (
      <span className={classes.iconCustomWrapper}>
        <SvgIcon type={type} className={classes.iconCustom} title={text} />
      </span>
    );
  };

  return (
    <FormControl component="fieldset" className={classes.root} fullWidth>
      <FormLabel component="legend">
        <Typography className={classes.label} variant="h2" component="h2">
          {label}
        </Typography>
      </FormLabel>

      <RadioGroup
        aria-label={label}
        name={name}
        value={watchValue}
        onChange={handleSelect}
      >
        {choices.map(({ key, label }) => {
          const isChecked = watchValue === key;

          return (
            <Box
              key={key}
              onClick={handleClick(key)}
              className={classNames(classes.optionContainer, {
                checked: isChecked,
              })}
            >
              <FormControlLabel
                control={
                  <Radio
                    color="default"
                    icon={<Icon type={key} classes={classes} title={label} />}
                  />
                }
                value={key}
                label={label}
              />
            </Box>
          );
        })}
      </RadioGroup>
    </FormControl>
  );
};

RadioField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  choices: PropTypes.array.isRequired,
  validations: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default RadioField;
